import React from "react";
import { ensurePdfFont } from "./pdfFont";
import { downloadBlob } from "./pdfDownload";
import { formatTimestampWithZone } from "./FormatDate";
import {
  createTriloChatPdfStyles,
  TRILOCHAT_PDF_COLORS,
} from "../scss/triloChatPdfStyles";
import {
  CHAT_MESSAGE_ROLE,
  DRAFT_STATUS,
  EXPORT_BLOB_REVOKE_DELAY_MS,
  EXPORT_CHAT_FILENAME_SUFFIX,
  EXPORT_DRAFTS_FILENAME_SUFFIX,
  EXPORT_SLUG_FALLBACK,
  EXPORT_SLUG_MAX_LENGTH,
  UNICODE_SUBSCRIPT_REGEX,
  UNICODE_SUBSCRIPT_TO_ASCII,
  UNICODE_SUPERSCRIPT_REGEX,
  UNICODE_SUPERSCRIPT_TO_ASCII,
} from "./triloChatConstants";
import triloLogo from "../assets/images/logos/TriloDocs-Logo-Horizontal-POSITIVE.png";

// PDF export for an MW conversation. Produces two documents: the conversation
// itself, and - when there are any - the drafts the user accepted.
//
// Written with React.createElement rather than JSX because @react-pdf/renderer
// is imported lazily, so its components are not in scope at module level.

// Builds the two document factories. Split from exportTriloChatPdfs so the
// renderer and the font are only resolved once per export.
export async function buildTriloChatPdfBuilders(
  sessionDate,
  exportedAt,
  draftStates
) {
  const { Document, Page, Text, View, Image, StyleSheet, pdf } =
    await import("@react-pdf/renderer");

  const bodyFont = await ensurePdfFont();
  const S = createTriloChatPdfStyles(StyleSheet, bodyFont);
  const { teal } = TRILOCHAT_PDF_COLORS;

  function inlineRuns(raw) {
    const segments = [];
    let rest = raw
      .replace(/[✓✔☑✅]/g, "Yes")
      .replace(/[✗✘☒❌❎]/g, "No")
      // Convert Unicode super/subscript chars to ^..^ / ~..~ so the small-font
      // branch below handles them - see triloChatConstants.js for why.
      .replace(
        UNICODE_SUPERSCRIPT_REGEX,
        m =>
          "^" +
          [...m].map(c => UNICODE_SUPERSCRIPT_TO_ASCII[c] ?? c).join("") +
          "^"
      )
      .replace(
        UNICODE_SUBSCRIPT_REGEX,
        m =>
          "~" +
          [...m].map(c => UNICODE_SUBSCRIPT_TO_ASCII[c] ?? c).join("") +
          "~"
      );
    let k = 0;
    while (rest) {
      const bm = /\*\*(.+?)\*\*/.exec(rest);
      const im = /\*(.+?)\*/.exec(rest);
      const sm = /\^([^\s^]+)\^?/.exec(rest);
      const sbm = /~([^\s~]+)~?/.exec(rest);
      let pick = null;
      const candidates = [
        bm && { bold: true, m: bm },
        im && { italic: true, m: im },
        sm && { sup: true, m: sm },
        sbm && { sub: true, m: sbm },
      ].filter(Boolean);
      if (candidates.length)
        pick = candidates.reduce((a, b) => (a.m.index <= b.m.index ? a : b));

      if (!pick) {
        segments.push(React.createElement(Text, { key: k++ }, rest));
        break;
      }
      if (pick.m.index > 0)
        segments.push(
          React.createElement(Text, { key: k++ }, rest.slice(0, pick.m.index))
        );

      let style, content;
      if (pick.bold) {
        style = { fontFamily: bodyFont, fontWeight: "bold" };
        content = pick.m[1];
      } else if (pick.italic) {
        style = { fontFamily: bodyFont, fontStyle: "italic" };
        content = pick.m[1];
      } else if (pick.sup) {
        style = { fontSize: 6, verticalAlign: "super" };
        content = pick.m[1];
      } else {
        style = { fontSize: 6, verticalAlign: "sub" };
        content = pick.m[1];
      }
      segments.push(React.createElement(Text, { key: k++, style }, content));
      rest = rest.slice(pick.m.index + pick.m[0].length);
    }
    return segments;
  }

  function mdToPDF(content) {
    const els = [];
    let k = 0;
    const lines = content.split("\n");
    let i = 0;

    const isTableRow = l =>
      /^\|/.test(l.trim()) && l.trim().lastIndexOf("|") > 0;
    const isSepRow = l => /^\|[\s\-:|]+\|/.test(l.trim());
    const parseRow = l =>
      l
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map(c => c.trim());

    while (i < lines.length) {
      const line = lines[i].trim();

      if (!line) {
        i++;
        continue;
      }

      if (/^---+$/.test(line)) {
        els.push(React.createElement(View, { key: k++, style: S.hr }));
        i++;
        continue;
      }

      const hm = line.match(/^(#{1,4})\s+(.*)/);
      if (hm) {
        const st = [S.h1, S.h2, S.h3, S.h4][hm[1].length - 1];
        els.push(
          React.createElement(Text, { key: k++, style: st }, inlineRuns(hm[2]))
        );
        i++;
        continue;
      }

      if (isTableRow(line)) {
        const tableLines = [];
        while (
          i < lines.length &&
          (isTableRow(lines[i].trim()) || isSepRow(lines[i].trim()))
        ) {
          tableLines.push(lines[i]);
          i++;
        }
        const rows = tableLines.filter(l => !isSepRow(l.trim()));
        const [headerRow, ...bodyRows] = rows;
        const headers = parseRow(headerRow);
        const dataRows = bodyRows.map(parseRow);

        const tableEls = [];
        const hCells = headers.map((h, ci) =>
          React.createElement(
            View,
            { key: ci, style: ci < headers.length - 1 ? S.tCell : S.tCellLast },
            React.createElement(Text, { style: S.tHeaderText }, inlineRuns(h))
          )
        );
        tableEls.push(
          React.createElement(
            View,
            { key: "h", style: S.tHeaderRow },
            ...hCells
          )
        );

        dataRows.forEach((row, ri) => {
          const cells = row.map((c, ci) =>
            React.createElement(
              View,
              { key: ci, style: ci < row.length - 1 ? S.tCell : S.tCellLast },
              React.createElement(Text, { style: S.tCellText }, inlineRuns(c))
            )
          );
          tableEls.push(
            React.createElement(
              View,
              { key: `r${ri}`, style: ri % 2 === 0 ? S.tRow : S.tRowAlt },
              ...cells
            )
          );
        });

        els.push(
          React.createElement(View, { key: k++, style: S.table }, ...tableEls)
        );
        continue;
      }

      if (/^[-*]\s/.test(line)) {
        els.push(
          React.createElement(
            Text,
            { key: k++, style: S.li },
            React.createElement(Text, { style: { color: teal } }, "• "),
            ...inlineRuns(line.replace(/^[-*]\s+/, ""))
          )
        );
        i++;
        continue;
      }
      if (/^\d+\.\s/.test(line)) {
        const num = line.match(/^(\d+)\./)[1];
        els.push(
          React.createElement(
            Text,
            { key: k++, style: S.li },
            `${num}. `,
            ...inlineRuns(line.replace(/^\d+\.\s+/, ""))
          )
        );
        i++;
        continue;
      }
      els.push(
        React.createElement(Text, { key: k++, style: S.para }, inlineRuns(line))
      );
      i++;
    }
    return els;
  }

  const header = React.createElement(
    View,
    { style: S.header, fixed: true },
    React.createElement(Image, { src: triloLogo, style: S.logo })
  );

  const footer = React.createElement(
    View,
    { style: S.footer, fixed: true },
    React.createElement(
      Text,
      { style: S.footerText },
      `Session: ${sessionDate}`
    ),
    React.createElement(
      Text,
      { style: S.footerText },
      `Exported: ${exportedAt}`
    )
  );

  const makeDocument = body =>
    React.createElement(
      Document,
      null,
      React.createElement(
        Page,
        { size: "A4", style: S.page },
        header,
        ...body,
        footer
      )
    );

  function makeDraftsBlob(acceptedDrafts) {
    const body = [];
    acceptedDrafts.forEach((d, i) =>
      body.push(
        React.createElement(
          View,
          { key: `d${i}`, style: S.draftCard },
          React.createElement(
            View,
            { style: S.draftRow },
            React.createElement(
              Text,
              { style: S.draftTitle },
              d.title || "Draft"
            ),
            React.createElement(
              Text,
              { style: d.wasEdited ? S.draftTagEdited : S.draftTagAccepted },
              d.wasEdited ? "Edited & Accepted" : "Accepted"
            )
          ),
          ...mdToPDF(d.content || "")
        )
      )
    );
    return pdf(makeDocument(body)).toBlob();
  }

  function makeChatBlob(messages) {
    const body = [];
    messages.forEach((m, i) => {
      if (!m.content) return;
      if (m.role === CHAT_MESSAGE_ROLE.user) {
        body.push(
          React.createElement(
            Text,
            { key: `u${i}`, style: S.userQ },
            `Q  ${m.content}`
          )
        );
      } else if (m.role === CHAT_MESSAGE_ROLE.assistant) {
        const draftEls = (m.drafts || []).map((draft, di) => {
          const st = draftStates[`${i}-${di}`];
          const content = st?.content ?? draft.content;
          const wasEdited = st?.wasEdited ?? false;
          const status = st?.status ?? DRAFT_STATUS.pending;
          const tag =
            status === DRAFT_STATUS.accepted
              ? React.createElement(
                  Text,
                  { style: wasEdited ? S.draftTagEdited : S.draftTagAccepted },
                  wasEdited ? "Edited & Accepted" : "Accepted"
                )
              : null;
          return React.createElement(
            View,
            { key: `draft${di}`, style: S.draftCard },
            React.createElement(
              View,
              { style: S.draftRow },
              React.createElement(Text, { style: S.draftTitle }, draft.title),
              tag
            ),
            React.createElement(
              Text,
              { style: S.draftContent },
              ...inlineRuns(content)
            )
          );
        });
        body.push(
          React.createElement(
            View,
            { key: `a${i}`, style: S.asstBlock },
            ...mdToPDF(m.content),
            ...draftEls
          )
        );
      }
    });
    return pdf(makeDocument(body)).toBlob();
  }

  return { makeDraftsBlob, makeChatBlob };
}

const exportSlug = title =>
  (title || EXPORT_SLUG_FALLBACK)
    .replace(/[^a-z0-9]/gi, "_")
    .slice(0, EXPORT_SLUG_MAX_LENGTH);

// Renders and downloads the conversation PDF, plus an accepted-drafts PDF when
// the session has any. Throws if the render fails - the caller decides what to
// show the user.
export async function exportTriloChatPdfs({ session, messages, draftStates }) {
  const acceptedDrafts = Object.values(draftStates).filter(
    d => d.status === DRAFT_STATUS.accepted
  );
  const slug = exportSlug(session?.title);

  const { makeDraftsBlob, makeChatBlob } = await buildTriloChatPdfBuilders(
    formatTimestampWithZone(session?.created_at),
    formatTimestampWithZone(new Date().toISOString()),
    draftStates
  );

  const download = (blob, filename) =>
    downloadBlob(blob, filename, {
      revokeDelayMs: EXPORT_BLOB_REVOKE_DELAY_MS,
    });

  download(
    await makeChatBlob(messages),
    `${slug}${EXPORT_CHAT_FILENAME_SUFFIX}`
  );

  if (acceptedDrafts.length > 0) {
    download(
      await makeDraftsBlob(acceptedDrafts),
      `${slug}${EXPORT_DRAFTS_FILENAME_SUFFIX}`
    );
  }
}
